// Every read the live ops map makes.
//
// ⚠ THE TRIP LAYER USED TO HAVE NO SOURCE AT ALL. LiveMapPage subscribed to the
// `trips:live` Realtime Broadcast channel and NOTHING EVER PUBLISHED TO IT —
// the only writer was the client app's Dev Panel "Live bridge", deleted with
// the mocks on 2026-08-08. The map rendered an empty trip layer behind a
// hardcoded "demo journey" that was switched on by default. See OQ-99.
//
// Both trip reads go through the `dispatch` Edge Function, which resolves the
// hub scope from the verified JWT and returns NO client column: /live-map is
// open to hub_manager, who may never reach client contact information through
// any path (rules/data-wall.md, migration 0086, pgTAP 122).

import { useQuery } from "@tanstack/react-query";
import { toManila } from "@bednbody/logic";
import {
  activeBookingClientPinsSchema,
  clientMapPinsSchema,
  type ActiveBookingClientPin,
  opsLiveTripsSnapshotSchema,
  opsLiveTripTrackSchema,
  staffLocationSnapshotSchema,
  type ClientMapPin,
  type OpsLiveTrip,
  type OpsLiveTripTrack,
} from "@bednbody/types";
import { parseHubPoint } from "@/features/hubs/hubs.hooks";
import { supabase } from "@/lib/supabase";
import {
  functionErrorMessage,
  functionErrorStatus,
  type ClockedInStaffMapItem,
  type HubMapItem,
  type StaffLocationFailure,
} from "./live-map.shared";

/** Protected map context: hubs are direct reads; staff pins come from the
 * operations-only Edge Function so client-facing roles never receive them. */
export function useLiveMapContext() {
  return useQuery({
    queryKey: ["live-map-context"],
    queryFn: async (): Promise<{
      hubs: HubMapItem[];
      clockedInStaff: ClockedInStaffMapItem[];
      staffLocationError: StaffLocationFailure | null;
    }> => {
      // ⚠ PILOT HUBS ONLY, WHICH IS ONE HUB TODAY (Demo Cainta).
      //
      // This map drew every hub row regardless of `is_pilot`, putting test and
      // dormant hubs on an operations surface beside live ones. That was not
      // cosmetic. A hub pin is drawn LARGER than a staff pin (circle-radius 8
      // vs 6 in LiveMapPage), so a rider clocked in AT a hub renders inside the
      // hub marker and reads as no rider at all — and clock-in is geofenced to
      // a hub, so that is the normal case, not an edge one. Two test hubs sat
      // within ~60 m of each other in Cainta and one of them shared a rider's
      // coordinates exactly; that is how "John is clocked in but has no pin on
      // the live map" was reported on 2026-08-18. Those two were deleted
      // (seed/remove_unused_hubs.sql); this stops the class of problem.
      //
      // Demo Taytay Hub stays in the database and stops appearing here. It
      // could not be deleted even if that were wanted: 14
      // `therapist_final_counts` rows sit behind
      // `trg_therapist_final_counts_protect` (0076), a BEFORE DELETE trigger
      // that raises unconditionally. Filtering is the right tool here; deleting
      // an immutability guarantee to tidy a map is not.
      const hubsResult = await supabase
        .from("hubs")
        .select("id, name, region, geo, is_pilot, service_radius_km")
        .eq("is_pilot", true)
        .order("name");
      if (hubsResult.error) throw hubsResult.error;

      const hubs = (hubsResult.data ?? []).flatMap((hub) => {
        const point = parseHubPoint(hub.geo);
        return point
          ? [
              {
                id: hub.id,
                name: hub.name,
                region: hub.region,
                isPilot: Boolean(hub.is_pilot),
                serviceRadiusKm: hub.service_radius_km,
                point,
              },
            ]
          : [];
      });
      const locationsResult = await supabase.functions.invoke("staff-location", {
        body: { data: {} },
      });
      const envelope = locationsResult.data as { ok?: unknown; data?: unknown } | null;
      const parsedLocations = staffLocationSnapshotSchema.safeParse(envelope?.data);

      // ⚠ THE ADVICE FOLLOWS THE FAULT. Three failures reach this banner and
      // they have three different next actions; one shared sentence about MFA
      // sent a reader after a permission problem that did not exist.
      let staffLocationError: StaffLocationFailure | null = null;
      if (locationsResult.error) {
        const status = functionErrorStatus(locationsResult.error);
        const refused = status === 401 || status === 403;
        staffLocationError = {
          message: await functionErrorMessage(locationsResult.error),
          // MFA belongs HERE and only here: 0021 gives staff-admin roles their
          // PostgREST role at aal2, so a session that never stepped up is
          // exactly what a 401/403 on this route looks like.
          hint: refused
            ? "Verify that this account has completed MFA as a hub manager, admin, or superadmin, then refresh."
            : "Check your connection, then refresh.",
        };
      } else if (!parsedLocations.success) {
        staffLocationError = {
          message: "The staff-location service returned an invalid response.",
          // Not the reader's to fix, and signing in again cannot touch it —
          // saying so is what stops the next hour going into an MFA dead end.
          hint: "The deployed service is out of step with this page, not your account — report it rather than signing in again.",
        };
      }
      const clockedInStaff = (parsedLocations.success ? parsedLocations.data : []).map((staff) => ({
        id: staff.staffId,
        name: staff.name,
        role: staff.role,
        hubId: staff.hubId,
        capturedAt: staff.capturedAt,
        locationState: staff.state,
        // TEND-024. The Edge Function has returned this since `0104`; the map
        // dropped it here, which is why the six statuses existed end to end and
        // still could not be seen.
        mapStatus: staff.mapStatus,
        opsStatus: staff.opsStatus ?? null,
        point: staff.point,
      }));
      return { hubs, clockedInStaff, staffLocationError };
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/** How stale a fix may be before a dot is a lie rather than a position. */
export const STALE_FIX_MS = 90_000;

/**
 * Live trips, one latest rider fix each.
 *
 * 5 seconds, matching `RIDER_POSITION_POLL_MS` in the client's own tracking
 * hook. An operations surface should never be showing an older position than
 * the customer on the phone is looking at.
 */
export const LIVE_TRIPS_POLL_MS = 5_000;

export function useLiveTrips() {
  return useQuery({
    queryKey: ["live-map-trips"],
    queryFn: async (): Promise<OpsLiveTrip[]> => {
      const result = await supabase.functions.invoke("dispatch/live-trips", { body: { data: {} } });
      if (result.error) throw new Error(await functionErrorMessage(result.error));
      const envelope = result.data as { data?: unknown } | null;
      // Never render an unvalidated payload — the same rule the Broadcast
      // reader had, kept now that the transport is trustworthy anyway.
      return opsLiveTripsSnapshotSchema.parse(envelope?.data);
    },
    refetchInterval: LIVE_TRIPS_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/**
 * One trip's DRIVEN path — the actual GPS breadcrumbs, not a Mapbox Directions
 * suggestion. On an ops map a road-snapped "optimal" line would quietly hide
 * the detour that is usually why someone opened the map.
 *
 * Fetched only for the selected trip: `rider_locations` is the highest-volume
 * table in the system and pulling every trip's history on a 5s poll is the
 * shape of a problem you cannot undo later.
 */
export function useLiveTripTrack(tripId: string | null) {
  return useQuery({
    queryKey: ["live-map-trip-track", tripId],
    enabled: tripId !== null,
    queryFn: async (): Promise<OpsLiveTripTrack> => {
      const result = await supabase.functions.invoke(`dispatch/live-trips/${tripId}/track`, {
        body: { data: {} },
      });
      if (result.error) throw new Error(await functionErrorMessage(result.error));
      const envelope = result.data as { data?: unknown } | null;
      return opsLiveTripTrackSchema.parse(envelope?.data);
    },
    refetchInterval: LIVE_TRIPS_POLL_MS,
  });
}

/**
 * The saved Client Pins layer — Super Admin only, fetched on toggle.
 *
 * `Continuous Location Monitoring.md` § Super Admin map: "Show saved Client
 * pins only through a separate Client Pins layer toggle."
 *
 * ⚠ NOT POLLED, AND THAT IS LOAD-BEARING. `ops_client_pins_snapshot` writes an
 * `audit_log` row per call, because § Security requires auditing Super Admin
 * ACCESS and not only changes. Saved pins do not move, so one fetch per toggle
 * is both correct and the only shape that keeps the audit trail readable — a
 * `refetchInterval` here would write a row every tick and bury the record of
 * who actually opened the layer. `enabled` is what keeps it off until asked.
 */
export function useClientPins(enabled: boolean) {
  return useQuery({
    queryKey: ["live-map-client-pins"],
    enabled,
    queryFn: async (): Promise<ClientMapPin[]> => {
      const result = await supabase.functions.invoke("staff-location/client-pins", {
        body: { data: {} },
      });
      if (result.error) {
        const status = functionErrorStatus(result.error);
        // 403 here is the rule working, not a fault: every non-superadmin who
        // reaches this is supposed to be refused. Say that plainly rather than
        // sending them to the MFA advice the staff pins use.
        throw new Error(
          status === 401 || status === 403
            ? "The Client Pins layer is Super Admin only."
            : await functionErrorMessage(result.error),
        );
      }
      const envelope = result.data as { ok?: unknown; data?: unknown } | null;
      return clientMapPinsSchema.parse(envelope?.data);
    },
    // The layer is a deliberate act, so a remount inside one sitting should not
    // re-audit. Long enough to survive navigation, short enough that a pin
    // saved today shows up in the same shift.
    staleTime: 10 * 60_000,
  });
}

/**
 * Every staff id with PUBLISHED duty today (Manila date) — added 2026-09-23
 * so the ops map can stop mixing demo/fixture accounts that are permanently
 * Offline in with staff actually on today's roster.
 *
 * ⚠ Reads `staff_schedule_entries` directly, the same table Schedules already
 * reads client-side (schedules.hooks.ts) — no new grant needed. A hub can
 * carry more than one PUBLISHED version of the same week (a republish), so
 * this keeps only the HIGHEST `version_number` per hub rather than every
 * published entry, matching the "latest published wins" rule the booking
 * engines already use server-side (`listLatestPublished`).
 *
 * This is a narrower signal than "dispatchable" (it does not check
 * `staff.active` or qualifications) — good enough to declutter a map, not a
 * capacity decision.
 */
export function useTodayDutyStaffIds() {
  return useQuery({
    queryKey: ["live-map-today-duty"],
    queryFn: async (): Promise<Set<string>> => {
      const today = toManila(new Date().toISOString(), "now").date;
      const { data, error } = await supabase
        .from("staff_schedule_entries")
        .select("staff_id, staff_schedule_versions!inner(hub_id, published_at, version_number)")
        .eq("schedule_date", today)
        .eq("kind", "duty")
        .not("staff_schedule_versions.published_at", "is", null);
      if (error) throw error;

      const latestVersionByHub = new Map<string, number>();
      for (const row of data ?? []) {
        const hubId = row.staff_schedule_versions.hub_id;
        const version = row.staff_schedule_versions.version_number;
        if (version > (latestVersionByHub.get(hubId) ?? -1)) latestVersionByHub.set(hubId, version);
      }

      const ids = new Set<string>();
      for (const row of data ?? []) {
        if (
          row.staff_schedule_versions.version_number ===
          latestVersionByHub.get(row.staff_schedule_versions.hub_id)
        ) {
          ids.add(row.staff_id);
        }
      }
      return ids;
    },
    // Published duty for today does not change minute to minute; refresh on
    // the same cadence as the staff/hub context it filters.
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * Today's open bookings for the Bookings tab and the attention rules — every
 * booking, not only the ones whose rider is already moving, so "waiting for a
 * rider" is visible before there is anything to draw.
 *
 * ⚠ NO CLIENT COLUMN, AND THAT IS WHAT MAKES ONE QUERY SERVE EVERY ROLE. The
 * select stays inside hub_manager's column grants on `bookings` (0005 + 0175),
 * and RLS scopes her to her own hub. Those grants are a column LIST:
 * `dispatch_due_at` and `completed_at` are not on it, and naming either here
 * fails the whole query for her — verified against the deployed grants
 * 2026-09-28. The status engine reads both server-side instead (0270).
 */
export function useLiveMapBookings() {
  return useQuery({
    queryKey: ["live-map-bookings"],
    queryFn: async () => {
      const now = Date.now();
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `id, ref, status, scheduled_at, hub_id, therapist_id, rider_id, booking_group_id,
           service_variants ( duration_minutes, services ( name ) )`,
        )
        .neq("status", "cancelled")
        .gte("scheduled_at", new Date(now - 18 * 3_600_000).toISOString())
        .lte("scheduled_at", new Date(now + 18 * 3_600_000).toISOString())
        .order("scheduled_at")
        .limit(300);
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type LiveMapBooking = NonNullable<ReturnType<typeof useLiveMapBookings>["data"]>[number];

/** Today's no-show reports — RLS scopes hub_manager to her hub's (0012). */
export function useLiveMapNoShows() {
  return useQuery({
    queryKey: ["live-map-no-shows"],
    queryFn: async () => {
      const since = new Date(Date.now() - 18 * 3_600_000).toISOString();
      const { data, error } = await supabase
        .from("no_show_reports")
        .select("id, party, created_at, booking_id, bookings ( ref, hub_id )")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
    refetchInterval: 30_000,
  });
}

/** Cash waiting to be received at a hub — the stuck-remittance signal (BL-38). */
export function useLiveMapPendingCash() {
  return useQuery({
    queryKey: ["live-map-pending-cash"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cash_remittances")
        .select("id, amount, status, hub_id, created_at")
        .eq("status", "pending")
        .gt("amount", 0);
      if (error) throw error;
      return data;
    },
    refetchInterval: 60_000,
  });
}

/**
 * BL-21 — therapists whose service is done and who are still waiting to be
 * collected for the ride back. `destination` tells a return from a hop to the
 * next client: `hub` and `origin` are the ways home (0111, 0139).
 *
 * The clock is the dispatch's own `created_at`, which 0237 writes when the
 * return is created — that is the moment the service ended and the ride was
 * owed, which is what BL-21 measures.
 */
export function useTherapistReturnWaits() {
  return useQuery({
    queryKey: ["live-map-return-waits"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("therapist_dispatches")
        .select(
          `id, hub_id, created_at, destination,
           therapist:staff!therapist_dispatches_therapist_id_fkey ( full_name ),
           rider:staff!therapist_dispatches_rider_id_fkey ( full_name )`,
        )
        .eq("status", "waiting_for_pick_up")
        .in("destination", ["hub", "origin", "dropoff"])
        .order("created_at");
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        hubId: row.hub_id,
        waitingSince: row.created_at,
        therapistName: row.therapist?.full_name ?? "A therapist",
        riderName: row.rider?.full_name ?? null,
      }));
    },
    refetchInterval: 60_000,
  });
}

/**
 * Which client each in-progress booking belongs to (0258) — Super Admin only,
 * audited per call. Like `useClientPins`, NOT polled: every fetch writes an
 * `audit_log` row, so it loads once per toggle and on an explicit refresh.
 */
export function useActiveBookingClientPins(enabled: boolean) {
  return useQuery({
    queryKey: ["live-map-active-client-pins"],
    enabled,
    queryFn: async (): Promise<ActiveBookingClientPin[]> => {
      const result = await supabase.functions.invoke("staff-location/active-booking-client-pins", {
        body: { data: {} },
      });
      if (result.error) {
        const status = functionErrorStatus(result.error);
        throw new Error(
          status === 401 || status === 403
            ? "The active clients layer is Super Admin only."
            : await functionErrorMessage(result.error),
        );
      }
      const envelope = result.data as { data?: unknown } | null;
      return activeBookingClientPinsSchema.parse(envelope?.data);
    },
    staleTime: 5 * 60_000,
  });
}
